import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Divider,
    Paper,
    InputAdornment
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIcon from '@mui/icons-material/Phone';
import PaymentService from '../../services/PaymentService';
import api from '../../services/api';

const SERVICE_AMOUNT = 10000; // ₹10,000

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

const FormSection = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`
}));

const steps = ['Business Details', 'Contact Info', 'Address', 'Review & Pay'];

const GmbCreationForm = ({ open, onClose }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        contact_phone: ''
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.contact_phone?.trim()) newErrors.contact_phone = 'Mobile number is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setError(null);

        try {
            const payload = {
                ...formData,
                business_name: 'GMB Request - ' + formData.contact_phone,
                contact_name: 'Requester',
                address_line1: 'Location via call',
                city: 'Pending',
                state: 'Pending',
                zip_code: '000000',
                country: 'India'
            };

            const result = await api.post('/customer/gmb-creation/create', payload);

            if (!result.success) {
                throw new Error(result.message || 'Failed to create order');
            }

            // Load Razorpay and open checkout
            await PaymentService.loadRazorpayScript();

            const options = {
                key: result.data.keyId,
                amount: result.data.amountPaise,
                currency: 'INR',
                order_id: result.data.orderId,
                name: 'Visibeen',
                description: result.data.description,
                image: window.location.origin + '/logo.png',
                prefill: {
                    contact: formData.contact_phone,
                    name: 'GMB Requester'
                },
                theme: { color: theme.palette.primary.main },
                handler: async (paymentResponse) => {
                    // Verify payment
                    try {
                        const verifyResult = await api.post('/customer/gmb-creation/verify-payment', {
                            order_id: result.data.orderId,
                            payment_id: paymentResponse.razorpay_payment_id,
                            signature: paymentResponse.razorpay_signature
                        });

                        if (verifyResult.success) {
                            setSuccess(true);
                        } else {
                            throw new Error(verifyResult.message || 'Payment verification failed');
                        }
                    } catch (verifyErr) {
                        setError(verifyErr.message || 'Payment verification failed');
                    }
                },
                modal: {
                    ondismiss: () => {
                        setLoading(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (err) {
            console.error('GMB Creation error:', err);
            setError(err.message || 'Failed to process request');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                contact_phone: ''
            });
            setErrors({});
            setError(null);
            setSuccess(false);
            onClose && onClose({ success });
        }
    };

    const renderSuccess = () => (
        <Box textAlign="center" py={4}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
                Thank you for your payment. Our team will create your GMB profile soon.
            </Typography>
            <Typography variant="body2" color="text.secondary">
                We'll contact you at <strong>+91 {formData.contact_phone}</strong> once your profile is ready.
            </Typography>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{
                p: 3,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#fff'
            }}>
                <Typography variant="h5" component="span" fontWeight={700} sx={{ display: 'block' }}>
                    {success ? '🎉 Success!' : "Request New GMB Profile"}
                </Typography>
                {!success && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        Don't have a Google Business Profile? We'll create one for you!
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {success ? (
                    renderSuccess()
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {error && (
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* 1. Requirements Checklist */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                background: '#f8fafc',
                                borderRadius: 3,
                                border: '1px solid #e2e8f0'
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 20 }} /> Service Prerequisites
                            </Typography>

                            <Grid container spacing={1.5}>
                                {[
                                    { title: 'Mandatory Docs', desc: 'GST or Udhyam Certificate copy.' },
                                    { title: 'Premises Photos', desc: '2 photos from different angles.' },
                                    { title: 'Street View', desc: '3 photos showing shop with neighbors.' },
                                    { title: 'Contact Proof', desc: 'Number on board & visiting card (Mandatory).' },
                                    { title: 'Website', desc: 'Company website (Optional).' }
                                ].map((item, idx) => (
                                    <Grid item xs={12} sm={6} key={idx}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#2563eb', mt: 0.3 }} />
                                            <Box>
                                                <Typography variant="caption" fontWeight={700} display="block" sx={{ lineHeight: 1.2, fontSize: '11px' }}>{item.title}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px', display: 'block' }}>{item.desc}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        {/* 2. Single Form Field */}
                        <Box>
                            <SectionTitle variant="subtitle1" sx={{ borderLeft: '4px solid #2563eb', pl: 1.5, mb: 2 }}>
                                Get Started
                            </SectionTitle>
                            <TextField
                                fullWidth
                                label="Mobile Number"
                                placeholder="91XXXXXXXX"
                                value={formData.contact_phone}
                                onChange={handleChange('contact_phone')}
                                error={!!errors.contact_phone}
                                helperText={errors.contact_phone}
                                required
                                autoFocus
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">+91</InputAdornment>
                                }}
                            />
                            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#1e40af', fontWeight: 600 }}>
                                Our experts will contact you soon for your business details.
                            </Typography>
                        </Box>

                        {/* 3. Payment Summary (More Compact) */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                borderRadius: 2,
                                border: '1px solid #bfdbfe',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Box>
                                <Typography variant="subtitle1" fontWeight={800} color="#1e40af">
                                    Service Fee: ₹{SERVICE_AMOUNT.toLocaleString('en-IN')}
                                </Typography>
                                <Typography variant="caption" color="#1e60af" sx={{ fontSize: '10px' }}>
                                    One-time payment for GMB setup & verification support
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Expert Guidance CTA (Premium Shrunk) */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            background: 'rgba(255, 248, 241, 0.5)',
                            borderRadius: '12px',
                            border: '1px dashed #ffe0b2'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #fff 0%, #fff7ed 100%)',
                                    boxShadow: '0 2px 4px rgba(230, 81, 0, 0.1)',
                                    color: '#e65100'
                                }}>
                                    <SupportAgentIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#9a3412', display: 'block', lineHeight: 1 }}>Have Questions?</Typography>
                                    <Typography variant="caption" sx={{ color: '#ea580c', fontSize: '10px' }}>Talk to an expert first</Typography>
                                </Box>
                            </Box>
                            <Button
                                size="small"
                                href="tel:+919876543210"
                                variant="outlined"
                                startIcon={<PhoneIcon sx={{ fontSize: 12 }} />}
                                sx={{
                                    color: '#e65100',
                                    borderColor: '#fed7aa',
                                    fontWeight: 700,
                                    fontSize: '11px',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    px: 1.5,
                                    py: 0.5,
                                    background: '#fff',
                                    boxShadow: '0 2px 6px rgba(230, 81, 0, 0.05)',
                                    '&:hover': {
                                        background: '#fff7ed',
                                        borderColor: '#e65100',
                                        boxShadow: '0 4px 10px rgba(230, 81, 0, 0.1)'
                                    }
                                }}
                            >
                                Call Support
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                {success ? (
                    <Button onClick={handleClose} variant="contained" size="large" sx={{ borderRadius: '50px', px: 4 }}>
                        Close
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={loading} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                borderRadius: '50px',
                                px: 5,
                                textTransform: 'none',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)'
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : `Pay ₹${SERVICE_AMOUNT.toLocaleString('en-IN')} & Start`}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default GmbCreationForm;
